import React, { useEffect, useRef } from "react";
import { clsx } from "clsx";
import {
    AudioOutlined,
    CheckCircleFilled,
    CheckOutlined,
    CopyOutlined,
    FilePdfOutlined,
    FlagOutlined,
    LoadingOutlined,
    PaperClipOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { Button } from "../../../design-system";
import type { ChatMessage } from "./types";
import classes from "./DrawerAssistant.module.less";

const BrandMark: React.FC = () => (
    <svg viewBox="0 0 110 61" fill="none" aria-label="Infinity Loop">
        <path
            d="M108.9083736,24.6111203L87.5611864,3.26393c-2.9444372-2.94444-7.7291564-2.94444-10.6735936,0l-36.6214753,36.4374449c-2.7604125,2.5763843-7.3611,5.1527686-11.0416518,5.1527686-4.0486047,0-8.0972093-1.840275-10.8576218-4.7847157-2.7604125-3.1284689-4.0486047-7.1770718-3.68055-11.4097047.7361111-6.8090172,6.2569343-12.145814,13.0659532-12.6978986h1.4722204c3.68055,0,7.1770753,1.2881922,9.7534596,3.8645782l6.4409625,6.4409625c.5520811.5520829,1.6562468.5520829,2.2083314,0l7.3611-7.3611c.5520811-.5520829.5520811-1.6562468,0-2.2083297l-4.4166593-4.4166602-2.0243067-2.0243006-.1840282-.1840273c-5.8888814-5.520825-13.8020625-8.2812375-21.7152471-7.5451273C13.3980991,3.8160124,2.908532,14.1215523,1.4363119,27.1875046c-.9201375,7.9131829,1.6562473,15.826364,6.993045,21.7152454,5.3367977,5.8888814,12.8819241,9.3854032,20.795107,9.3854032,10.121516,0,18.2187218-6.8090189,20.4270532-8.8333221l31.6527314-31.284675c.5520811-.5520829,1.6562503-.5520829,2.2083314,0l11.2256747,11.2256782c.5520811.5520829.5520811,1.6562468,0,2.2083314l-11.2256747,11.2256782c-.5520811.5520811-1.6562503.5520811-2.2083314,0l-8.4652622-8.4652657c-.5520811-.5520811-1.6562503-.5520811-2.2083314,0l-7.3611,7.3611c-.5520811.5520811-.5520811,1.6562468,0,2.2083314l13.6180378,13.6180343c1.4722186,1.6562468,3.4965183,2.3923561,5.3367933,2.3923561s3.8645747-.7361093,5.3368003-2.2083314l21.3471872-21.3471907c4.0486064-3.8645747,3.68055-8.0972076,0-11.7777576h0Z"
            fill="#24b2ee"
        />
    </svg>
);

const TypingDots: React.FC = () => (
    <div className={classes.typing}>
        <span />
        <span />
        <span />
    </div>
);

const AttachmentCard: React.FC<{
    filename: string;
    size: string;
    progress: number;
    uploading: boolean;
}> = ({ filename, size, progress, uploading }) => (
    <div className={classes.attachmentCard}>
        <div className={classes.attachmentIconWrap}>
            <FilePdfOutlined className={classes.attachmentIcon} />
        </div>
        <div className={classes.attachmentBody}>
            <div className={classes.attachmentName}>{filename}</div>
            <div className={classes.attachmentMeta}>
                {uploading ? (
                    <>
                        <LoadingOutlined className={classes.attachmentSpinner} />
                        Uploading… {Math.round(progress)}%
                    </>
                ) : (
                    <>
                        <CheckCircleFilled className={classes.attachmentReady} />
                        {size} · queued for the analyst
                    </>
                )}
            </div>
            {uploading && (
                <div className={classes.attachmentProgress}>
                    <span style={{ width: `${progress}%` }} />
                </div>
            )}
        </div>
    </div>
);

const EmailCard: React.FC<{
    subject: string;
    body: string;
}> = ({ subject, body }) => (
    <div className={classes.emailCard}>
        <div className={classes.emailHeader}>
            <span className={classes.emailLabel}>Subject</span>
            <span className={classes.emailSubject}>{subject}</span>
        </div>
        <pre className={classes.emailBody}>{body}</pre>
    </div>
);

const MessageBubble: React.FC<{
    message: ChatMessage;
    onQueue?: () => void;
    onCopy?: () => void;
    copied?: boolean;
    hasBeenCopied?: boolean;
}> = ({ message, onQueue, onCopy, copied, hasBeenCopied }) => {
    if (message.typing) {
        return (
            <div className={clsx(classes.message, classes.messageFromBot)}>
                <div className={clsx(classes.messageAvatar, classes.messageAvatarBrand)}>
                    <BrandMark />
                </div>
                <div className={classes.messageBody}>
                    <TypingDots />
                </div>
            </div>
        );
    }

    const isBot = message.role === "bot";
    const isDeflect = message.kind === "deflect";
    const isEmail = message.kind === "email";
    const showCopy =
        isBot && (!!message.text || !!message.email) && !!onCopy;
    const showQueue =
        isBot &&
        (message.kind === "answer" || message.kind === "deflect" || message.kind === "email") &&
        message.exchangeIdx !== undefined &&
        !!onQueue;

    return (
        <div
            className={clsx(
                classes.message,
                isBot ? classes.messageFromBot : classes.messageFromUser
            )}
        >
            <div
                className={clsx(
                    classes.messageAvatar,
                    isBot && classes.messageAvatarBrand
                )}
            >
                {isBot ? <BrandMark /> : "D"}
            </div>
            <div className={classes.messageBody}>
                {message.text && (
                    <div className={classes.bubbleContent}>{message.text}</div>
                )}
                {message.attachment && (
                    <AttachmentCard
                        filename={message.attachment.filename}
                        size={message.attachment.size}
                        progress={message.attachment.progress}
                        uploading={message.attachment.uploading}
                    />
                )}
                {isEmail && message.email && (
                    <EmailCard
                        subject={message.email.subject}
                        body={message.email.body}
                    />
                )}
                {(showCopy || showQueue) && (
                    <div className={classes.messageActions}>
                        {showCopy && (
                            <button
                                type="button"
                                className={clsx(
                                    classes.actionButton,
                                    copied && classes.actionButtonDone,
                                    isEmail &&
                                        !copied &&
                                        !hasBeenCopied &&
                                        classes.actionButtonHighlight
                                )}
                                onClick={onCopy}
                            >
                                {copied ? (
                                    <>
                                        <CheckOutlined /> Copied
                                    </>
                                ) : (
                                    <>
                                        <CopyOutlined /> Copy
                                    </>
                                )}
                            </button>
                        )}
                        {showQueue && (
                            <button
                                type="button"
                                className={clsx(
                                    classes.actionButton,
                                    message.queued && classes.actionButtonDone,
                                    isDeflect && !message.queued && classes.actionButtonHighlight
                                )}
                                onClick={onQueue}
                                disabled={message.queued}
                            >
                                {message.queued ? (
                                    <>
                                        <CheckOutlined /> Queued for analyst
                                    </>
                                ) : (
                                    <>
                                        <FlagOutlined /> Queue for analyst
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

interface Props {
    messages: ChatMessage[];
    recordingTranscript: string | null;
    canRunNext: boolean;
    onRunNext: (mode: "typed" | "spoken") => void;
    onQueueExchange: (exchangeIdx: number) => void;
    onAttachDoc: () => void;
}

export const AskChatPanel: React.FC<Props> = ({
    messages,
    recordingTranscript,
    canRunNext,
    onRunNext,
    onQueueExchange,
    onAttachDoc,
}) => {
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [copiedId, setCopiedId] = React.useState<string | null>(null);
    const [copiedIds, setCopiedIds] = React.useState<Set<string>>(() => new Set());

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [messages, recordingTranscript]);

    useEffect(() => {
        const el = textAreaRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [recordingTranscript]);

    const placeholder =
        recordingTranscript !== null
            ? "Recording — speak naturally…"
            : "Ask me anything, or tap the mic…";

    const inputValue = recordingTranscript ?? "";
    const isRecording = recordingTranscript !== null;

    const handleCopy = async (id: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // clipboard unavailable — swallow; demo doesn't need a fallback
        }
        setCopiedId(id);
        setCopiedIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
        window.setTimeout(() => {
            setCopiedId((cur) => (cur === id ? null : cur));
        }, 1800);
    };

    const copyTextFor = (m: ChatMessage): string | null => {
        if (m.email) return `Subject: ${m.email.subject}\n\n${m.email.body}`;
        if (m.text) return m.text;
        return null;
    };

    return (
        <div className={classes.chatPanel}>
            <div className={classes.messages} ref={messagesContainerRef}>
                {messages.map((m) => (
                    <MessageBubble
                        key={m.id}
                        message={m}
                        onQueue={
                            m.exchangeIdx !== undefined
                                ? () => onQueueExchange(m.exchangeIdx!)
                                : undefined
                        }
                        onCopy={(() => {
                            const text = copyTextFor(m);
                            return text ? () => handleCopy(m.id, text) : undefined;
                        })()}
                        copied={copiedId === m.id}
                        hasBeenCopied={copiedIds.has(m.id)}
                    />
                ))}
            </div>

            <div className={classes.composer}>
                <div
                    className={clsx(
                        classes.inputShell,
                        isRecording && classes.inputShellRecording
                    )}
                >
                    <textarea
                        ref={textAreaRef}
                        className={classes.textArea}
                        placeholder={placeholder}
                        value={inputValue}
                        readOnly
                        rows={3}
                    />
                    <div className={classes.inputControls}>
                        <button
                            type="button"
                            className={classes.iconButton}
                            title="Attach document for next round"
                            onClick={onAttachDoc}
                        >
                            <PaperClipOutlined />
                        </button>
                        <button
                            type="button"
                            className={clsx(
                                classes.iconButton,
                                classes.micButton,
                                isRecording && classes.micButtonListening
                            )}
                            title="Tap to record"
                            onClick={() => onRunNext("spoken")}
                            disabled={!canRunNext || isRecording}
                        >
                            <AudioOutlined />
                        </button>
                        {isRecording && (
                            <div className={classes.listeningHint}>
                                <span className={classes.waveform}>
                                    <span />
                                    <span />
                                    <span />
                                    <span />
                                    <span />
                                </span>
                                Recording…
                            </div>
                        )}
                        <div className={classes.spacer} />
                        <button
                            type="button"
                            className={clsx(classes.iconButton, classes.sendButton)}
                            title="Send"
                            onClick={() => onRunNext("typed")}
                            disabled={!canRunNext || isRecording}
                        >
                            <SendOutlined />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
